import { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import AppMenu from '../../../../../../../../components/common/AppMenu';
import AppPrintTable from '../../../../../../../../components/common/AppPrintTable';
import AppConfirmationDialog from '../../../../../../../../components/common/AppConfirmationDialog';
import { SVG } from '../../../../../../../../constants/assetLists';
import { useOnBoardingContext } from '../../../../../../../../context/onBoarding/OnBoardingContextProvider';
import { exportToExcelFromColumns } from '../../../../../../../../utils/exportToExcel';
import {
  DepartmentFormValues,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../../types';
import type { PrintColumn } from '../../../../../../../../components/common/AppPrintTable';
import {
  getLocationDepartments,
  getLocationNames,
  getLocationRoles,
  getRoleTypeName,
} from '../../../../../../../../utils';
import { ClientOption } from './NotifyDeptTreeSelect';
import moment from 'moment';

type Props = {
  users: OnboardingUserFormValues[];
  departments: DepartmentFormValues[];
  roles: RoleFormValues[];
  clients: ClientOption[];
};

function MenuSection({ users, departments, roles, clients }: Props) {
  const { allRoleTypes } = useOnBoardingContext();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showPrintConfirmation, setShowPrintConfirmation] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'משתמשים',
  });

  const getDeptNames = (user: OnboardingUserFormValues): string => {
    if (user.departmentId === 'all') return 'כל המחלקות';
    if (!Array.isArray(user.departmentId) || !user.departmentId.length)
      return '-';
    const locationDepartments = getLocationDepartments(
      clients,
      user.clientIds,
      user.branchIds,
      user.subBranchIds,
    );
    return (
      user.departmentId
        .map((id) => locationDepartments.find((d) => d.id === id)?.name || id)
        .join(', ') || '-'
    );
  };

  const getRoleName = (user: OnboardingUserFormValues): string => {
    const combinedRoles = getLocationRoles(
      clients,
      user.clientIds,
      user.branchIds,
      user.subBranchIds,
    );
    const role = combinedRoles?.find((rl) => rl.id === user.roleId);
    const roleType = allRoleTypes?.data?.find(
      (rt) => rt._id === role?.roleType,
    );
    return getRoleTypeName(roleType) || '-';
  };

  const EXPORT_COLUMNS = useMemo<PrintColumn<OnboardingUserFormValues>[]>(
    () => [
      {
        title: 'שם משתמש',
        accessor: (r) => `${r.firstName} ${r.lastName}`,
        width: 160,
      },
      { title: 'אימייל', accessor: (r) => r.email, width: 200 },
      {
        title: 'טלפון',
        accessor: (r) =>
          r.phone
            ? !r.phone.startsWith('972')
              ? '972' + r.phone
              : r.phone
            : '-',
        width: 130,
      },
      { title: 'תפקיד', accessor: (r) => getRoleName(r), width: 150 },
      {
        title: 'סטאטוס חשבון',
        accessor: (r) => (r.status === 'active' ? 'פעיל' : 'לא פעיל'),
        width: 110,
      },
      // {
      //   title: 'ארגון',
      //   accessor: (r) =>
      //     r.clientIds
      //       .map((id) => clients.find((c) => c.id === id)?.name ?? id)
      //       .join(', '),
      //   width: 140,
      // },
      {
        title: 'בית עסק',
        accessor: (r) => {
          const locations = getLocationNames(
            clients,
            r.clientIds,
            r.branchIds,
            r.subBranchIds,
            true,
          );
          if (!(locations as string[])?.length) return '-';
          return (locations as string[])?.join(', ') || '-';
        },
        width: 160,
      },
      {
        title: 'מחלקה',
        accessor: (r) => getDeptNames(r),
        width: 140,
      },
      {
        title: 'תאריך יצירה',
        accessor: (r) => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
        width: 130,
      },
      { title: 'משוייך לקבוצה', accessor: () => '-', width: 130 },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [departments, roles, allRoleTypes, clients],
  );

  const menuItems = useMemo(
    () => [
      {
        title: 'יצוא רשימה לאקסל',
        value: 'export',
        icon: SVG.csvGreenIcon,
        disabled: users.length === 0,
      },
      {
        title: 'הדפסת רשימה',
        value: 'print',
        icon: SVG.printerBlackIcon,
        disabled: users.length === 0,
      },
    ],
    [users.length],
  );

  const handleMenuClick = (val: string) => {
    setMenuAnchorEl(null);
    if (val === 'export') {
      exportToExcelFromColumns(EXPORT_COLUMNS, users, 'Users_Onboarding.xlsx');
    } else if (val === 'print') {
      setShowPrintConfirmation(true);
    }
  };

  return (
    <>
      <AppMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onOpen={(e) => setMenuAnchorEl(e.currentTarget as HTMLElement)}
        onClose={() => setMenuAnchorEl(null)}
        items={menuItems}
        onItemClick={handleMenuClick}
        showDivider
        // dividerIndex={1}
        itemIconWidth={24}
        itemIconHeight={24}
      />

      <div style={{ display: 'none' }}>
        <AppPrintTable<OnboardingUserFormValues>
          ref={printRef}
          title="רשימת משתמשים"
          subtitle={`סה״כ משתמשים: ${users.length}`}
          columns={EXPORT_COLUMNS}
          rows={users}
        />
      </div>

      <AppConfirmationDialog
        open={showPrintConfirmation}
        onClose={() => setShowPrintConfirmation(false)}
        onConfirm={() => {
          setShowPrintConfirmation(false);
          handlePrint();
        }}
        icon={
          <img
            src={SVG.printerAquaLgIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="הדפסה"
        subTitle="שליחה להדפסה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
        bordered={false}
      />
    </>
  );
}

export default MenuSection;
